import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Seo } from '../../components/seo';
import { useLocale, useT } from '../../i18n/locale-context';
import { LocaleLink } from '../../i18n/locale-link';
import { localePath } from '../../i18n/locale-path';
import { startContent } from '../../content/landings';
import { fetchLandingStats, landingStatsQueryKey } from '../../lib/landing-client';
import { trackLandingCta, trackLandingView } from '../../lib/landing-tracking';
import { formatCount, tendersWord } from '../../lib/format';
import { LandingHeader } from './layout/landing-header';
import { LandingFooter } from './layout/landing-footer';
import { StickyCta } from './layout/sticky-cta';
import { LandingSection } from './layout/landing-section';
import { Counters } from './sections/counters';
import { CategoryStrip } from './sections/category-strip';
import { CompareTable } from './sections/compare-table';
import { PricingThree } from './sections/pricing-three';
import { LotCard } from './sections/lot-card';
import { Faq } from './sections/faq';
import { Trust } from './sections/trust';
import { Illustration } from './sections/illustration';
import { SourcesStrip } from './sections/sources-strip';

/**
 * `/start` и `/uz/start` — конверсионный лендинг тарифа Pro.
 *
 * Монтируется ВНЕ `AppLayout` (см. app.tsx): у страницы своя шапка, свой
 * футер и своя липкая CTA-полоса, внутри сайдбарной оболочки было бы два
 * хедера. Разметка следует макету
 * `docs/superpowers/specs/2026-08-14-landings-mockups/landing-pro.html`,
 * включая иллюстративные мокапы правой колонки («Рыночные данные», чат в
 * Telegram, «Поставщики заказчика», карточка с документами) — это снимки
 * реального Pro-кабинета от 13.08.2026, отрисовываются компонентом
 * `<Illustration>` из `content.heroIllustration`/`content.how[].illustration`
 * (см. `LandingIllustration` в content/landings/types.ts).
 */
export function LandingStart() {
  const { locale } = useLocale();
  const t = useT();
  const c = startContent(locale);

  const { data } = useQuery({
    queryKey: landingStatsQueryKey(locale),
    queryFn: () => fetchLandingStats(locale),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    trackLandingView('start', locale);
  }, [locale]);

  const cta = (label: string) => trackLandingCta('start', locale, label);

  // Отступление 9: «Сегодня» → «за неделю» ({count} — последние 7 ПОЛНЫХ
  // календарных дней (UTC), не один день и не скользящее окно 24ч, см.
  // counters.newLastWeekLabel в content-файле и комментарий в
  // LandingStatsRepository.counters()).
  //
  // Без данных (пререндер не смог получить статистику — см. scripts/prerender.ts,
  // «empty-shell fallback») заголовок не рисуем вовсе, а не подставляем 0: «добавили
  // 0 тендеров» — это утверждение о каталоге, а не отсутствие данных (тот же
  // принцип, что и в sections/counters.tsx).
  const finalH2 = data
    ? c.finalH2
        .replace('{count}', formatCount(data.newLastWeek))
        .replace('{word}', tendersWord(data.newLastWeek, locale))
    : null;

  // Отступление 3 (правка ревью): «97 открытых международных тендеров» было
  // захардкожено рядом с живым счётчиком data.ifiActive выше на странице — они
  // расходятся при первом же изменении каталога (тот же класс бага, что и
  // устаревшее «около 2 400» в search.empty.title, i18n/locales/ru.ts). Без
  // данных показываем «—», как и плитки-счётчики, а не 0; форма слова при
  // отсутствии данных берётся как для «много» (родительный падеж — словарная
  // форма по умолчанию).
  const ifiBody = c.ifiBody
    .replace('{count}', data ? formatCount(data.ifiActive) : '—')
    .replace('{word}', tendersWord(data ? data.ifiActive : 0, locale));

  return (
    <div className="min-h-screen bg-bone text-ink">
      <Seo
        title={c.pageTitle}
        description={c.metaDescription}
        canonical="/start"
        lang={locale}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: c.faq.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }}
      />
      {/* Отступление 10/12 (ревью владельца): шапка ведёт на /catalog, не на
          #pricing, и больше не показывает телефон (остаётся в футере). */}
      <LandingHeader
        nav={c.nav}
        ctaLabel={c.ctaHeader}
        ctaHref="/catalog"
        onCtaClick={() => cta('header')}
      />

      {/* ============ 1. Первый экран ============ */}
      <section id="hero" className="pb-11 pt-[52px]">
        <div className="mx-auto grid max-w-[1120px] gap-10 px-5 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
          <div>
            <span className="mb-3 inline-block text-[12.5px] font-extrabold uppercase tracking-[0.08em] text-brand">
              {c.heroKicker}
            </span>
            <h1 className="mb-3.5 text-[clamp(31px,5.6vw,50px)] font-extrabold leading-[1.12] tracking-[-0.02em]">
              {c.heroH1}
            </h1>
            <p className="max-w-[560px] text-[clamp(16.5px,2.2vw,19px)] text-ink-soft">
              {c.heroLead}
            </p>
            {/* На телефоне кнопки — во всю ширину и в столбик, главная сверху:
                прижатые к тексту они читаются как случайные. С sm возвращается
                строка макета. */}
            {/* Отступление 10 (ревью владельца, 2026-08-18): primary теперь
                сама ведёт на /catalog («Перейти к площадке»), а не /plans —
                вторая кнопка перестала быть дублем и репойнтится на #pricing
                (единственное место, которое ещё продаёт тариф). */}
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <LocaleLink
                to="/catalog"
                onClick={() => cta('hero')}
                className="block w-full rounded-lg border-[1.5px] border-brand bg-brand px-6 py-3.5 text-center text-base font-bold text-white no-underline transition-colors hover:border-brand-mid hover:bg-brand-mid sm:inline-block sm:w-auto"
              >
                {c.ctaPrimary}
              </LocaleLink>
              <a
                href="#pricing"
                onClick={() => cta('hero-pricing')}
                className="block w-full rounded-lg bg-brand-light px-6 py-3.5 text-center text-base font-bold text-brand no-underline transition-colors hover:bg-[#DFEBE9] sm:inline-block sm:w-auto"
              >
                {c.ctaSecondary}
              </a>
            </div>
            <p className="mt-3 text-[13.5px] text-ink-soft">{c.heroMicro}</p>
            <LocaleLink
              to="/catalog"
              onClick={() => cta('searchbar')}
              className="mt-6 flex max-w-[560px] items-center gap-2.5 rounded-xl border border-edge bg-bone-surface px-4 py-3.5 text-[15px] text-ink-soft no-underline shadow-m-sm transition-colors hover:border-brand"
            >
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                className="flex-none text-ink-soft"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              {c.heroSearchPlaceholder}
              <span className="ml-auto rounded-md bg-brand-light px-2 py-[3px] text-[11.5px] font-extrabold tracking-[0.03em] text-brand">
                AI
              </span>
            </LocaleLink>
          </div>

          <div className="grid max-w-[420px] gap-3.5 lg:max-w-none lg:justify-self-end lg:[width:min(100%,420px)]">
            {data?.featuredLot && (
              <LotCard
                lot={data.featuredLot}
                onClick={() => cta('hero-tender')}
                lockLabel={t('landing.lotCard.lockLabel')}
                activeLabel={t('landing.lotCard.activeLabel')}
                daysLeftTemplate={t('landing.lotCard.daysLeft')}
                budgetLabel={t('list.col.budget')}
                bidSecurityLabel={t('landing.lotCard.bidSecurityLabel')}
                deadlineLabel={t('tender.deadline.closes')}
                contactLabel={t('landing.lotCard.contactLabel')}
                docsLabel={t('landing.lotCard.docsLabel')}
              />
            )}
            <Illustration data={c.heroIllustration} />
          </div>
        </div>
      </section>

      {/* ============ Источники ============ */}
      <SourcesStrip
        label={c.sourcesLabel}
        sources={c.sources}
        moreLabel={c.sourcesMoreLabel}
        updateNote={c.sourcesUpdateNote}
        sourceCount={data?.sourceCount}
      />

      {/* ============ 2. Счётчики ============ */}
      {/* Первая плитка несёт ДВА числа: «N тендеров открыто сейчас · M
          добавили за неделю». `accent` — вторая половина подписи, зелёная. */}
      <Counters
        items={[
          {
            value: data?.activeTenders,
            label: c.counters.activeLabel,
            accent:
              data == null
                ? null
                : `${formatCount(data.newLastWeek)} ${c.counters.newLastWeekLabel}`,
            href: '/catalog',
          },
          { value: data?.awardedTotal, label: c.counters.awardedLabel, href: '/results' },
          {
            value: data?.participantRecords,
            label: c.counters.participantsLabel,
            href: '/results',
          },
          { value: data?.ifiActive, label: c.counters.ifiLabel, href: '/ifi' },
        ]}
        onClick={cta}
      />

      {/* ============ 2b. Факт — 1 из 3 без победителя ============ */}
      {/* Отступление 11 (2026-08-20, ПЕРЕСМОТРЕНО 2026-08-24): измеренная
          оценка та же, что и хиро-блок /ip (см. комментарий над `factBig` в
          ip.ru.ts; перепроверка 24.08 — в start.ru.ts). Была одной серой
          строкой — по решению владельца развёрнута в полноценную карточку с
          CTA: строка была практически невидима и не работала как аргумент.
          По-прежнему НЕ через `LandingSection` (её нижний отступ — ритм
          полноценных секций); карточка визуально повторяет факт-бенд /ip
          (bg-brand-light), но добавляет заголовок и кнопку в каталог. */}
      <section className="pb-2 pt-5">
        <div className="mx-auto max-w-[1120px] px-5">
          <div className="flex flex-col gap-4 rounded-2xl border border-edge bg-brand-light p-6 shadow-m-sm sm:flex-row sm:items-center sm:gap-6">
            <div className="flex-none text-[clamp(38px,5.5vw,54px)] font-extrabold leading-none tracking-[-0.02em] text-brand">
              {c.factBig}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[clamp(17px,2.2vw,20px)] font-extrabold leading-[1.2]">
                {c.factTitle}
              </p>
              <p className="mt-1.5 max-w-[560px] text-[15px] text-ink-soft">{c.factBody}</p>
            </div>
            <LocaleLink
              to="/catalog"
              onClick={() => cta('fact')}
              className="block w-full flex-none rounded-lg border-[1.5px] border-brand bg-brand px-5 py-3 text-center text-[15px] font-bold text-white no-underline transition-colors hover:border-brand-mid hover:bg-brand-mid sm:w-auto"
            >
              {c.factCta}
            </LocaleLink>
          </div>
        </div>
      </section>

      {/* ============ 3. Знакомо? ============ */}
      <LandingSection top="lg">
        <h2 className="mb-3 text-[clamp(26px,4.2vw,38px)] font-extrabold leading-[1.12] tracking-[-0.02em]">
          {c.painsTitle}
        </h2>
        <div className="mt-[26px] grid gap-4 md:grid-cols-3">
          {c.pains.map((p) => (
            <div
              key={p.title}
              className="rounded-2xl border border-edge bg-bone-surface p-6 shadow-m-sm"
            >
              <h3 className="mb-2 text-[18px] font-extrabold leading-[1.12]">{p.title}</h3>
              <p className="text-[15px] text-ink-soft">{p.body}</p>
            </div>
          ))}
        </div>
      </LandingSection>

      {/* ============ 3b. Категории и регионы ============ */}
      <CategoryStrip
        kicker={c.nicheSectionKicker}
        title={c.nicheSectionTitle}
        lead={c.nicheSectionLead}
        categories={data?.topCategories ?? []}
        remaining={data?.remainingCategories ?? 0}
        moreLabel={c.moreCategoriesLabel}
        regionsLabel={c.regionsLabel}
        regions={c.regions}
        onClick={cta}
      />

      {/* ============ 4. Что меняет Pro ============ */}
      <LandingSection id="how" top="sm">
        <span className="mb-3 inline-block text-[12.5px] font-extrabold uppercase tracking-[0.08em] text-brand">
          {c.howKicker}
        </span>
        <h2 className="mb-3 text-[clamp(26px,4.2vw,38px)] font-extrabold leading-[1.12] tracking-[-0.02em]">
          {c.howTitle}
        </h2>
        <div className="mt-11 grid gap-11">
          {/* Средний блок (i === 1) идёт в макете «реверсом»: на широком
              экране иллюстрация слева, текст справа (`.jtbd.rev .mock{order:-1}`
              в макете). Остальные два — как обычно. На мобильном порядок
              всегда «текст → иллюстрация», реверс — только с lg. */}
          {c.how.map((item, i) => (
            <div key={item.title} className="grid items-center gap-8 lg:grid-cols-2 lg:gap-14">
              <div className={`max-w-[640px] ${i === 1 ? 'lg:order-2' : ''}`}>
                {/* Номер шага на телефоне встаёт В СТРОКУ с заголовком: своей
                    строкой он съедал вертикаль впустую. Не центрируем — ниже
                    идут абзац и чек-лист по левому краю, и центр сбил бы ритм
                    чтения. С lg возвращается кружок над заголовком, как в макете. */}
                <div className="mb-2.5 flex items-start gap-3 lg:mb-0 lg:block">
                  <span className="inline-flex h-[30px] w-[30px] flex-none items-center justify-center rounded-full bg-brand text-sm font-extrabold text-white lg:mb-3.5">
                    {i + 1}
                  </span>
                  <h3 className="text-[clamp(21px,2.8vw,27px)] font-extrabold leading-[1.12] lg:mb-2.5">
                    {item.title}
                  </h3>
                </div>
                <p className="text-base text-ink-soft">{item.body}</p>
                <ul className="mt-3.5 grid list-none gap-2.5">
                  {item.bullets.map((b) => (
                    <li
                      key={b}
                      className="relative pl-[26px] text-[15px] before:absolute before:left-0 before:font-extrabold before:text-status-active-text before:content-['✓']"
                    >
                      {b}
                    </li>
                  ))}
                </ul>
                {item.links.length > 0 && (
                  <div className="mt-3.5 flex flex-wrap gap-3">
                    {item.links.map((link) => (
                      <LocaleLink
                        key={link.href}
                        to={link.href}
                        onClick={() => cta(`how-${link.href.replace('/', '')}`)}
                        className="inline-block rounded-lg bg-brand-light px-4 py-2.5 text-sm font-bold text-brand no-underline transition-colors hover:bg-[#DFEBE9]"
                      >
                        {link.label}
                      </LocaleLink>
                    ))}
                  </div>
                )}
              </div>
              <div className={i === 1 ? 'lg:order-1' : ''}>
                <Illustration data={item.illustration} />
              </div>
            </div>
          ))}
        </div>
      </LandingSection>

      {/* ============ 5. Что бесплатно, а что в Pro ============ */}
      <CompareTable
        title={c.compareTitle}
        lead={c.compareLead}
        headFree={c.compareHeadFree}
        headPro={c.compareHeadPro}
        groups={c.compareGroups}
        ctaLabel={c.ctaPrimary}
        onCtaClick={() => cta('table')}
      />

      {/* ============ 6. МФИ ============ */}
      <LandingSection id="ifi" top="sm">
        <div className="rounded-2xl border border-edge bg-bone-surface p-8 shadow-m-sm">
          <div className="grid items-center gap-4 md:grid-cols-2">
            <div>
              <span className="mb-3 inline-block text-[12.5px] font-extrabold uppercase tracking-[0.08em] text-brand">
                {c.ifiKicker}
              </span>
              <h2 className="mb-3 text-[clamp(22px,3vw,30px)] font-extrabold leading-[1.12] tracking-[-0.02em]">
                {c.ifiTitle}
              </h2>
              <p className="max-w-[520px] text-[15.5px] text-ink-soft">{ifiBody}</p>
              <LocaleLink
                to="/ifi"
                onClick={() => cta('ifi')}
                className="mt-6 block w-full rounded-lg bg-brand-light px-6 py-3.5 text-center text-base font-bold text-brand no-underline transition-colors hover:bg-[#DFEBE9] sm:inline-block sm:w-auto sm:text-left"
              >
                {c.ifiCta}
              </LocaleLink>
            </div>
            <LocaleLink to="/ifi" onClick={() => cta('ifi-tender')} className="block no-underline">
              <Illustration data={c.ifiIllustration} />
            </LocaleLink>
          </div>
        </div>
      </LandingSection>

      {/* ============ 7. Тарифы ============ */}
      <PricingThree
        title={c.pricingTitle}
        lead={c.pricingLead}
        periodMonthly={c.periodMonthly}
        periodYearly={c.periodYearly}
        periodYearlyBadge={c.periodYearlyBadge}
        plans={c.plans}
        payNote={c.payNote}
        onCtaClick={cta}
        ipCrossLinkText={c.ipCrossLinkText}
        ipCrossLinkLabel={c.ipCrossLinkLabel}
      />

      {/* ============ 8. Кто за этим стоит ============ */}
      <Trust title={c.teamTitle} members={c.team} fineprint={c.fineprint} onActionClick={cta} />

      {/* ============ 9. FAQ ============ */}
      <Faq title={c.faqTitle} items={c.faq} />

      {/* ============ 10. Финал ============ */}
      <LandingSection top="sm">
        <div className="rounded-2xl bg-ink px-7 py-[52px] text-center">
          {finalH2 && (
            <h2 className="mb-3 text-[clamp(26px,4.2vw,38px)] font-extrabold leading-[1.12] tracking-[-0.02em] text-white">
              {finalH2}
            </h2>
          )}
          <p className="mx-auto mt-2.5 max-w-[600px] text-white/75">{c.finalLead}</p>
          {/* Отступление 10: единственная кнопка — раньше вторая («Смотреть
              каталог бесплатно») уже вела на /catalog, и после переадресации
              первой туда же обе стали дублем; убрана целиком (см. types.ts). */}
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-center">
            <LocaleLink
              to="/catalog"
              onClick={() => cta('final')}
              className="block w-full rounded-lg border-[1.5px] border-white bg-white px-6 py-3.5 text-center text-base font-bold text-ink no-underline transition-colors hover:border-bone hover:bg-bone sm:inline-block sm:w-auto"
            >
              {c.finalCtaPrimary}
            </LocaleLink>
          </div>
        </div>
      </LandingSection>

      <LandingFooter links={c.footerLinks} legal={c.footerLegal} />
      {/* Отступление 10: ведёт на /catalog. StickyCta рендерит обычный <a> без
          LocaleLink (см. sticky-cta.tsx) — локаль подставляем здесь. */}
      <StickyCta
        title={c.stickyTitle}
        subtitle={c.stickySubtitle}
        cta={c.stickyCta}
        href={localePath('/catalog', locale)}
        watchId="hero"
        onClick={() => cta('sticky')}
      />
    </div>
  );
}
